import { useMemo } from "react";
import { FaCamera } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import {
  normalizeStorytellingDisplay,
  getStorytellingNameStyle,
  getStorytellingNamePartColors,
  getStorytellingQuestionStyle,
  getStorytellingDescriptionStyle,
  STORYTELLING_BACKGROUND_STYLES,
} from "./storytellingUtils";
import { resolveStorytellingNameParts, formatStorytellingChapterLabel } from "./storytellingPresentation";
import { getVisibleStorytellingHeaderFields } from "./storytellingHeaderMeta";
import "./StorytellingPhonePreview.css";

const ROMAN_NUMERALS = [
  "I",
  "II",
  "III",
  "IV",
  "V",
  "VI",
  "VII",
  "VIII",
  "IX",
  "X",
];

function getRomanLabel(index, showEntryNumbers) {
  if (!showEntryNumbers) return "";
  return ROMAN_NUMERALS[index] || String(index + 1);
}

function StorytellingPhonePreview({
  restaurantName,
  address,
  city,
  serviceType,
  stories = [],
  display,
  headerMeta,
  menuButtonColor,
  previewImages = {},
  uploadsServer,
  compact = false,
}) {
  const { t } = useTranslation();
  const settings = useMemo(
    () => normalizeStorytellingDisplay(display, menuButtonColor),
    [display, menuButtonColor]
  );

  const accent = settings.accentColor || menuButtonColor || "#b08d57";
  const nameStyle = getStorytellingNameStyle(settings, "preview");
  const nameColors = getStorytellingNamePartColors(settings);
  const questionStyle = getStorytellingQuestionStyle(settings, "preview");
  const descriptionStyle = getStorytellingDescriptionStyle(settings, "preview");
  const { primary, accent: accentPart } = resolveStorytellingNameParts(
    restaurantName || "",
    settings
  );

  const visibleHeaderFields = useMemo(
    () => getVisibleStorytellingHeaderFields(headerMeta || {}),
    [headerMeta]
  );
  const leftHeaderFields = visibleHeaderFields.filter((field) => field.side === "left");
  const rightHeaderFields = visibleHeaderFields.filter((field) => field.side === "right");
  const hasHeaderMeta = visibleHeaderFields.length > 0;

  const visibleStories = stories.filter(
    (item, index) =>
      String(item.question || "").trim() ||
      String(item.description || "").trim() ||
      item.image ||
      previewImages[index]
  );

  const getImageSrc = (entry, index) => {
    if (previewImages[index]) return previewImages[index];
    if (entry?.image) return `${uploadsServer}/restaurant/${entry.image}`;
    return null;
  };

  const shellStyle = {
    background: STORYTELLING_BACKGROUND_STYLES[settings.backgroundStyle] || "#ffffff",
    "--storytelling-gold": accent,
    "--storytelling-cream": "#f5f1ea",
  };

  return (
    <div
      className={[
        "storytelling-phone-preview",
        compact ? "storytelling-phone-preview--compact" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="storytelling-phone-preview__meta">
        <span className="storytelling-phone-preview__live">
          <span className="storytelling-phone-preview__live-dot" aria-hidden="true" />
          {t("wizardPreviewLive")}
        </span>
      </div>

      <div className="storytelling-phone-preview__device">
        <div className="storytelling-phone-preview__screen" style={shellStyle}>
          <header
            className={[
              "storytelling-preview-hero",
              hasHeaderMeta ? "storytelling-preview-hero--with-meta" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <div className="storytelling-preview-hero__grid">
              {hasHeaderMeta ? (
                <div className="storytelling-preview-hero__side storytelling-preview-hero__side--left">
                  {leftHeaderFields.map((field) => (
                    <div className="storytelling-preview-hero__meta-item" key={field.key}>
                      <span
                        className="storytelling-preview-hero__meta-label"
                        style={{ color: accent }}
                      >
                        {field.label}
                      </span>
                      {field.value ? (
                        <span className="storytelling-preview-hero__meta-value">
                          {field.value}
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="storytelling-preview-hero__center">
                <div className="storytelling-preview-hero__eyebrow">
                  <span className="storytelling-preview-hero__rule" aria-hidden="true" />
                  <span className="storytelling-preview-hero__label">
                    {t("storytellingOurStory")}
                  </span>
                  <span className="storytelling-preview-hero__rule" aria-hidden="true" />
                </div>

                <h2 className="storytelling-preview-hero__title" style={nameStyle}>
                  {primary ? (
                    <span
                      className="storytelling-preview-hero__title-part storytelling-preview-hero__title-part--primary"
                      style={{ color: nameColors.primary }}
                    >
                      {primary}
                    </span>
                  ) : null}
                  {accentPart ? (
                    <span
                      className="storytelling-preview-hero__title-part storytelling-preview-hero__title-part--accent"
                      style={{ color: nameColors.accent }}
                    >
                      {accentPart}
                    </span>
                  ) : null}
                  {!primary && !accentPart ? restaurantName : null}
                </h2>
              </div>

              {hasHeaderMeta ? (
                <div className="storytelling-preview-hero__side storytelling-preview-hero__side--right">
                  {rightHeaderFields.map((field) => (
                    <div className="storytelling-preview-hero__meta-item" key={field.key}>
                      <span
                        className="storytelling-preview-hero__meta-label"
                        style={{ color: accent }}
                      >
                        {field.label}
                      </span>
                      {field.value ? (
                        <span className="storytelling-preview-hero__meta-value">
                          {field.value}
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </header>

          <div className="storytelling-preview-stories">
            {visibleStories.length === 0 ? (
              <p className="storytelling-preview-empty">{t("storytellingPreviewEmpty")}</p>
            ) : (
              visibleStories.map((story, index) => {
                const imageSrc = getImageSrc(story, index);
                const romanLabel = getRomanLabel(index, settings.showEntryNumbers);
                const isReversed = index % 2 === 1;

                return (
                  <article
                    key={`preview-story-${index}`}
                    className={[
                      "storytelling-preview-split",
                      isReversed ? "storytelling-preview-split--reversed" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <div className="storytelling-preview-split__media">
                      {romanLabel ? (
                        <span className="storytelling-preview-split__watermark" aria-hidden="true">
                          {romanLabel}
                        </span>
                      ) : null}
                      <div className="storytelling-preview-split__photo-ring">
                        <div className="storytelling-preview-split__photo-frame">
                          {imageSrc ? (
                            <img src={imageSrc} alt="" className="storytelling-preview-split__photo" />
                          ) : (
                            <div className="storytelling-preview-split__photo-placeholder">
                              <FaCamera size={18} aria-hidden="true" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="storytelling-preview-split__copy">
                      {settings.showEntryNumbers && romanLabel ? (
                        <div className="storytelling-preview-split__badge">
                          <span className="storytelling-preview-split__badge-mark">{romanLabel}</span>
                          <span className="storytelling-preview-split__badge-label">
                            {formatStorytellingChapterLabel(
                              story.chapterLabel,
                              romanLabel,
                              t
                            )}
                          </span>
                        </div>
                      ) : null}

                      <h3
                        className="storytelling-preview-split__question"
                        style={questionStyle}
                      >
                        {story.question || t("storytellingQuestion")}
                      </h3>
                      <span className="storytelling-preview-split__rule" aria-hidden="true" />
                      <div className="storytelling-preview-split__description-block">
                        <span
                          className="storytelling-preview-split__description-quote storytelling-preview-split__description-quote--open"
                          aria-hidden="true"
                        >
                          &ldquo;
                        </span>
                        <p
                          className="storytelling-preview-split__description"
                          style={descriptionStyle}
                        >
                          {story.description || t("storytellingDescription")}
                        </p>
                        <span
                          className="storytelling-preview-split__description-quote storytelling-preview-split__description-quote--close"
                          aria-hidden="true"
                        >
                          &rdquo;
                        </span>
                      </div>
                    </div>
                  </article>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default StorytellingPhonePreview;
